import { Alert, Box, Collapse, Stack } from "@mui/material";
import { type FC, useCallback, useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import { useBrandTones } from "../../modules/brand-kit/hooks/use-brand-tones";
import { useFontSelections } from "../../modules/brand-kit/hooks/use-font-selections";
import { BrandingBar } from "../../modules/branding-bar/branding-bar";
import { ContentPreviews } from "../../modules/content-previews/content-previews";
import { CreateContentCard } from "../../modules/create-content-card/create-content-card";
import { PromptSummary } from "../../modules/create-content-card/prompt-summary";
import { useAccount } from "../../modules/generic/hooks/use-account";
import { useAccountIndustry } from "../../modules/generic/hooks/use-account-industry";
import { Page } from "../../modules/layout/components/page";
import { useMultiformat } from "../../modules/multiformat/hooks/use-multi-format";
import { ContentHoldingDialog } from "./content-holding-dialog";
import { PreviewsSkeletons } from "./preview-skeletons";
import { useCustomTemplates } from "./use-custom-templates";

const CreateContentPreviewPage: FC = () => {
  const { data: multiformat, error: multiformatError } = useMultiformat();
  const { data: account, error: accountError } = useAccount();
  const { data: industry, error: industryError, loading: industryLoading } = useAccountIndustry();
  const { data: fontSelections, loading: fontSelectionsLoading } = useFontSelections();
  const { data: customTemplates, loading: customTemplatesLoading } =
    useCustomTemplates(multiformat);
  const { data: brandTones, loading: brandTonesLoading } = useBrandTones();
  const [searchParams] = useSearchParams();
  const [pageReady, setPageReady] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [useSelectedLogo, setUseSelectedLogo] = useState(false);
  const [holdingDialogDismissed, setHoldingDialogDismissed] = useState(false);

  const contentTypeName = useMemo(() => searchParams.get("format"), [searchParams]);
  const answer = useMemo(() => searchParams.get("answer"), [searchParams]);
  const db = useMemo(() => searchParams.get("db"), [searchParams]);
  const fileId = useMemo(() => searchParams.get("file_id"), [searchParams]);
  const imageStyles = useMemo(() => searchParams.get("image_styles"), [searchParams])?.split(",");
  const tone = useMemo(() => searchParams.get("tone"), [searchParams]);

  const handleReset = useCallback(() => {
    setUseSelectedLogo(false);
  }, []);

  const handleLogoChange = useCallback(() => {
    setUseSelectedLogo(true);
  }, []);

  const handlePageReady = useCallback(() => {
    setPageReady(true);
  }, []);

  const multiformatConfig = multiformat?.content_types.find((c) => c.name === contentTypeName);
  return (
    <Page title="Create Content Preview | ChangeEngine">
      <Box sx={{ position: "relative", minHeight: "2em" }}>
        <PromptSummary
          onClick={() => setShowForm(true)}
          sx={{ position: "absolute", width: "100%" }}
        />
        <Collapse in={showForm} sx={{ position: "relative" }}>
          <CreateContentCard onClose={() => setShowForm(false)} />
        </Collapse>
      </Box>
      <Stack gap={4} sx={(theme) => ({ [theme.breakpoints.down("md")]: { m: 3 }, m: 5 })}>
        <Stack direction="row-reverse">
          <BrandingBar
            compact={true}
            useSelectedLogo={useSelectedLogo}
            onLogoChange={handleLogoChange}
            onReset={handleReset}
          />
        </Stack>
        {multiformatConfig?.design_huddle?.dimensions &&
        contentTypeName &&
        account &&
        !industryLoading &&
        !customTemplatesLoading &&
        !fontSelectionsLoading &&
        !brandTonesLoading ? (
          <ContentPreviews
            multiformatConfig={multiformatConfig}
            answer={answer ?? ""}
            useSelectedLogo={useSelectedLogo}
            customTemplates={customTemplates}
            designer={account.designer}
            industry={industry}
            fontSelections={fontSelections}
            db={db}
            fileId={fileId ?? undefined}
            imageStyles={imageStyles}
            brandTones={brandTones}
            tone={tone ?? undefined}
            handlePageReady={handlePageReady}
            holdingDialogOpen={!holdingDialogDismissed}
          />
        ) : (
          multiformatConfig?.design_huddle?.dimensions && (
            <PreviewsSkeletons multiformatConfig={multiformatConfig} />
          )
        )}
        {(multiformatError || accountError || industryError) && <Error />}
      </Stack>
      {multiformatConfig && (
        <Box sx={{ justifyContent: "center" }}>
          <ContentHoldingDialog
            pageReady={pageReady}
            multiformatConfig={multiformatConfig}
            onDismiss={() => setHoldingDialogDismissed(true)}
          />
        </Box>
      )}
    </Page>
  );
};

export default CreateContentPreviewPage;

const Error: FC = () => {
  return (
    <Alert severity="error">
      Sorry, we encountered a problem trying to load this page. Please try again.
    </Alert>
  );
};
